import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useAuth } from '../App';
import { clients as clientsApi, getWsUrl } from '../api';
import { Search, CheckSquare, Square, Download, Upload, AlertCircle, CheckCircle, XCircle, Loader } from 'lucide-react';

const DOC_TYPES = [
  { key: 'AIS', label: 'AIS PDF' },
  { key: 'TIS', label: 'TIS PDF' },
  { key: '26AS_PDF', label: '26AS PDF' },
  { key: '26AS_TEXT', label: '26AS Text' },
];

const FY_OPTIONS = [
  { value: '2025-26', label: 'FY 2025-26 (AY 2026-27)' },
  { value: '2026-27', label: 'FY 2026-27 (AY 2027-28)' },
];

export default function Downloads() {
  const { t } = useTranslation();
  const { activeOffice } = useAuth();

  // Client selection
  const [allClients, setAllClients] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // Quick CSV
  const [tempClients, setTempClients] = useState([]);
  const [useTemp, setUseTemp] = useState(false);

  // Config
  const [docTypes, setDocTypes] = useState(['AIS', 'TIS', '26AS_PDF', '26AS_TEXT']);
  const [fy, setFy] = useState('2025-26');

  // Download session
  const [phase, setPhase] = useState('select'); // select | running | done
  const [progress, setProgress] = useState({ done: 0, total: 0, action: '', client: '', name: '' });
  const [clientStatuses, setClientStatuses] = useState({});
  const [failed, setFailed] = useState([]);
  const [zipReady, setZipReady] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // CAPTCHA
  const [captchaData, setCaptchaData] = useState(null);
  const [liveScreen, setLiveScreen] = useState(null);
  const [screenLabel, setScreenLabel] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');

  const wsRef = useRef(null);

  // Load clients
  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clientsApi.list({ office: activeOffice, status: 'active', limit: 500 });
      setAllClients(res.data.clients);
    } catch (e) {}
    setLoading(false);
  }, [activeOffice]);

  useEffect(() => { loadClients(); setSelected(new Set()); }, [loadClients]);

  const filtered = allClients.filter(c =>
    !search || c.pan.toLowerCase().includes(search.toLowerCase()) ||
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    c.client_code.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelect = (id) => setSelected(p => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(c => c.id)));
  };
  const toggleDoc = (key) => setDocTypes(p => p.includes(key) ? p.filter(d => d !== key) : [...p, key]);

  // Quick CSV upload for temp clients
  const handleTempCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split('\n').slice(1).filter(Boolean);
    const parsed = lines.map(line => {
      const [pan, password, dob] = line.split(',').map(s => s.trim());
      return { pan: pan?.toUpperCase(), password, dob: dob || '' };
    }).filter(c => c.pan && c.password && c.pan.length === 10);
    setTempClients(parsed);
    setUseTemp(true);
    toast.success(`${parsed.length} clients loaded from CSV`);
  };

  const totalSelected = useTemp ? tempClients.length : selected.size;
  const totalFiles = totalSelected * docTypes.length;

  // Start download
  const startDownload = async () => {
    if (docTypes.length === 0) return toast.error('Select at least one document type');
    if (totalSelected === 0) return toast.error('Select at least one client');

    const sid = crypto.randomUUID();
    setSessionId(sid);
    setPhase('running');
    setProgress({ done: 0, total: totalSelected, action: 'Initializing...', client: '', name: '' });
    setClientStatuses({});
    setFailed([]);
    setZipReady(false);
    setCaptchaData(null);

    // Build WS params
    const params = {
      doc_types: docTypes.join(','),
      fy,
    };
    if (useTemp) {
      params.temp_clients = JSON.stringify(tempClients);
    } else {
      params.client_ids = Array.from(selected).join(',');
    }

    const wsUrl = getWsUrl(sid, params);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      switch (data.type) {
        case 'progress':
          setProgress({ done: data.done, total: data.total, action: data.action, client: data.client, name: data.name });
          break;
        case 'screenshot':
          setLiveScreen(data.image);
          setScreenLabel(data.label || '');
          break;
        case 'captcha':
          setCaptchaData({ image: data.image, client: data.client });
          setCaptchaInput('');
          break;
        case 'client_done':
          setClientStatuses(p => ({ ...p, [data.client]: data.status }));
          setProgress(prev => ({ ...prev, done: data.done, total: data.total }));
          break;
        case 'error':
          setClientStatuses(p => ({ ...p, [data.client]: 'failed' }));
          break;
        case 'complete':
          setFailed(data.failed || []);
          setZipReady(true);
          setPhase('done');
          ws.close();
          break;
        case 'fatal_error':
          toast.error('Fatal error: ' + data.reason);
          setPhase('select');
          break;
        default: break;
      }
    };

    ws.onerror = () => toast.error('Connection error. Please try again.');
    ws.onclose = () => { if (phase === 'running') setPhase('select'); };
  };

  const submitCaptcha = () => {
    if (!captchaInput.trim()) return;
    wsRef.current?.send(JSON.stringify({ type: 'captcha_answer', answer: captchaInput.trim() }));
    setCaptchaData(null);
  };

  const skipCaptchaClient = () => {
    wsRef.current?.send(JSON.stringify({ type: 'skip_client' }));
    setCaptchaData(null);
  };

  const downloadZip = () => {
    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/downloads/file/${sessionId}`;
    window.open(url, '_blank');
  };

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  // ─── SELECT PHASE ──────────────────────────────────────────────────────────
  if (phase === 'select') return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)', marginBottom: 6 }}>{t('downloads')}</h1>
      <p style={{ color: 'var(--gray)', marginBottom: 24, fontSize: 13 }}>{t('automationRunning').split('.')[0]}.</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
        {/* Client list */}
        <div>
          {/* Quick CSV option */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--primary)', fontSize: 14 }}>{t('uploadCsvQuick')}</div>
                <div style={{ fontSize: 12, color: 'var(--gray)' }}>Download without adding to database</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {useTemp && (
                  <button className="btn btn-secondary btn-sm" onClick={() => { setUseTemp(false); setTempClients([]); }}>
                    Clear CSV
                  </button>
                )}
                <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                  <Upload size={13} /> Load CSV
                  <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleTempCSV} />
                </label>
              </div>
            </div>
            {useTemp && tempClients.length > 0 && (
              <div className="alert alert-success" style={{ marginBottom: 0 }}>
                ✓ {tempClients.length} clients loaded from CSV — these will be used for download
              </div>
            )}
          </div>

          {/* Database clients */}
          {!useTemp && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--gray)' }} />
                  <input className="input" style={{ paddingLeft: 32 }} placeholder="Search clients..."
                    value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <button className="btn btn-secondary btn-sm" onClick={toggleAll}>
                  {selected.size === filtered.length && filtered.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
                  {t('selectAll')}
                </button>
              </div>
              <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: 32 }}><div className="spinner" /></div>
                ) : filtered.map(c => (
                  <div key={c.id} onClick={() => toggleSelect(c.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                    cursor: 'pointer', borderBottom: '1px solid var(--border)',
                    background: selected.has(c.id) ? 'var(--accent-light)' : 'white',
                  }}>
                    <input type="checkbox" className="checkbox" checked={selected.has(c.id)} onChange={() => {}} />
                    <span className="badge badge-blue" style={{ minWidth: 60 }}>{c.client_code}</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>{c.pan}</span>
                    <span style={{ color: 'var(--gray)', fontSize: 13 }}>{c.name || ''}</span>
                  </div>
                ))}
                {!loading && filtered.length === 0 && (
                  <div style={{ textAlign: 'center', color: 'var(--gray)', padding: 32 }}>No clients found</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Config panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="section-label">{t('documentTypes')}</div>
            {DOC_TYPES.map(d => (
              <label key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>
                <input type="checkbox" className="checkbox" checked={docTypes.includes(d.key)} onChange={() => toggleDoc(d.key)} />
                <span style={{ fontSize: 14 }}>{d.label}</span>
              </label>
            ))}
          </div>

          <div className="card">
            <div className="section-label">{t('financialYear')}</div>
            <select className="input" value={fy} onChange={e => setFy(e.target.value)}>
              {FY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div className="card" style={{ background: 'var(--accent-light)', border: '1px solid #BFDBFE' }}>
            <div style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 600, marginBottom: 6 }}>Summary</div>
            <div style={{ fontSize: 13, color: 'var(--gray)' }}>
              {totalSelected} {t('clients')} × {docTypes.length} docs = <strong>{totalFiles} {t('files')}</strong>
            </div>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }}
            onClick={startDownload} disabled={totalSelected === 0 || docTypes.length === 0}>
            <Download size={16} /> {t('startDownload')}
          </button>
        </div>
      </div>
    </div>
  );

  // ─── RUNNING PHASE ─────────────────────────────────────────────────────────
  if (phase === 'running') return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)', marginBottom: 24 }}>{t('downloadProgress')}</h1>

      <div className="alert alert-info" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Loader size={16} className="spinner" style={{ animation: 'spin 1s linear infinite' }} />
          {t('automationRunning')}
        </div>
      </div>

      {/* CAPTCHA Widget */}
      {captchaData && (
        <div className="card" style={{ marginBottom: 20, border: '2px solid var(--accent)', background: 'var(--accent-light)' }}>
          <div style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: 12, fontSize: 16 }}>
            ⚠ {t('captchaRequired')} — Client: {captchaData.client}
          </div>
          <img src={`data:image/png;base64,${captchaData.image}`} alt="CAPTCHA"
            style={{ border: '1px solid var(--border)', borderRadius: 6, marginBottom: 12, display: 'block', maxHeight: 80 }} />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input className="input" style={{ maxWidth: 200 }} placeholder={t('enterCaptcha')}
              value={captchaInput} onChange={e => setCaptchaInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submitCaptcha()} autoFocus />
            <button className="btn btn-primary" onClick={submitCaptcha}>{t('submit')}</button>
            <button className="btn btn-secondary" onClick={skipCaptchaClient}>{t('skipClient')}</button>
          </div>
        </div>
      )}

      {/* Progress */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{progress.done} / {progress.total} clients</span>
          <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{pct}%</span>
        </div>
        <div className="progress-bar-bg" style={{ marginBottom: 14 }}>
          <div className="progress-bar" style={{ width: `${pct}%` }} />
        </div>
        {progress.client && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--gray)', fontSize: 13 }}>
            <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
            <strong>{progress.client}</strong> — {progress.action}
          </div>
        )}
      </div>

      
              {/* Live Browser View */}
              {liveScreen && (
                <div className="card" style={{marginBottom: 20}}>
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10}}>
                    <div style={{fontWeight: 700, color: 'var(--primary)', fontSize: 14}}>
                      Live Browser View
                    </div>
                    <span style={{fontSize: 11, color: 'var(--gray)', background: 'var(--gray-light)', padding: '2px 8px', borderRadius: 20}}>
                      {screenLabel}
                    </span>
                  </div>
                  <img src={"data:image/png;base64," + liveScreen}
                    style={{width: "100%", borderRadius: 8, border: "1px solid var(--border)", display: "block"}}
                    alt="browser view" />
                </div>
              )}
              {/* Per-client status */}
      {Object.keys(clientStatuses).length > 0 && (
        <div className="card" style={{ maxHeight: 300, overflowY: 'auto' }}>
          <div className="section-label" style={{ marginBottom: 10 }}>Completed Clients</div>
          {Object.entries(clientStatuses).map(([code, status]) => (
            <div key={code} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
              {status === 'success' ? <CheckCircle size={15} color="var(--success)" /> : <XCircle size={15} color="var(--danger)" />}
              <span style={{ fontWeight: 600 }}>{code}</span>
              <span className={`badge ${status === 'success' ? 'badge-success' : 'badge-danger'}`}>{status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ─── DONE PHASE ────────────────────────────────────────────────────────────
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)', marginBottom: 24 }}>{t('downloadComplete')}</h1>

      <div className="alert alert-success" style={{ marginBottom: 20, fontSize: 15 }}>
        ✓ Download complete! {progress.total - failed.length} of {progress.total} clients downloaded successfully.
      </div>

      <button className="btn btn-success" style={{ marginBottom: 24, padding: '12px 24px', fontSize: 16 }} onClick={downloadZip}>
        <Download size={18} /> {t('downloadZip')}
      </button>

      {failed.length > 0 && (
        <div className="card">
          <div style={{ fontWeight: 700, color: 'var(--danger)', marginBottom: 12 }}>
            <AlertCircle size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {t('failedClients')} ({failed.length})
          </div>
          <table className="table">
            <thead><tr><th>Client</th><th>PAN</th><th>Document</th><th>Reason</th></tr></thead>
            <tbody>
              {failed.map((f, i) => (
                <tr key={i}>
                  <td>{f.client}</td>
                  <td style={{ fontFamily: 'monospace' }}>{f.pan}</td>
                  <td>{f.doc}</td>
                  <td style={{ color: 'var(--danger)', fontSize: 12 }}>{f.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button className="btn btn-secondary" style={{ marginTop: 20 }} onClick={() => setPhase('select')}>
        ← Start New Download
      </button>
    </div>
  );
}
